import { useCallback, useState } from "react"
import { portalApi } from "../../../core/api/api"
import { ConfirmProductDeactivation, DeleteConfirmation_contentStyles, DeleteConfirmation_iconButtonStyles } from "../../../components/shared/DeleteConfirmation"
import { IconButton, Modal, Spinner, Stack, Text } from "@fluentui/react"
import { strings } from "../../../localization/strings";
import { SecondaryButton } from "../../../components/shared/buttons/SecondaryButton";
import { DangerButton } from "../../../components/shared/buttons/DangerButton";
import { Solutions } from "../../../core/store/typings/Solutions";

export type TOrganizationDeactivatorProps = {
    organizationName: string
    organizationId: string
    customerId?: string
    productId?: string
    onCanceled: () => void
}

export const OrganizationDeactivator: React.FC<TOrganizationDeactivatorProps> = props => {
    const organizationId = props.organizationId;
    const productId = props.productId ?? '';
    const customerId = props.customerId ?? '';
    const [initialConfirmationDone, setInitialConfirmationDone] = useState<boolean>(false)
    const [confirmPmc, setConfirmPmc] = useState<boolean>(false);
    const [ otherConfirmMessage, setOtherConfirmMessage] = useState<string>("");
    const [canDeactivateCustomer, setCanDeactivateCustomer] = useState<boolean>(false);
    const [canDeactivateProduct, setCanDeactivateProduct] = useState<boolean>(false);

    const deactivateOrganization = useCallback( async () => {
        setConfirmPmc( false);
        const result = await portalApi.settings.organization.activateOrganization(false);
        console.log( "Organization deactivated:", result)
        window.location.reload();
    }, [])

    const onPmcConfirmed = useCallback( async () => {
        setConfirmPmc( false);
        if( canDeactivateProduct) {
            const result = await portalApi.settings.organization.updatePiceaProduct( productId, true);
            console.log( "Product deactivated:", result)
        }
        if( canDeactivateCustomer) {
            const result = await portalApi.settings.organization.updatePiceaCustomer( customerId, true);
            console.log( "Customer deactivated:", result)
        }

        deactivateOrganization();
    }, [deactivateOrganization, canDeactivateCustomer, canDeactivateProduct, productId, customerId])

    const deactivatePmcProduct = useCallback( async () => {
        setInitialConfirmationDone( true);
        setCanDeactivateCustomer(false);
        setCanDeactivateProduct(false);
        
        if( '' === customerId && '' === productId) {
            //  No product/customer to deactivate.
            deactivateOrganization();
            return;
        }

        let onlyOnlineCustomer = false;
        let onlyOnlineProduct = false;
        let _canDeactivateProduct = false;
        let _canDeactivateCustomer = false;

        if( '' !== customerId) {
            const cc = await portalApi.settings.organization.getPiceaCustomerOrganizations( customerId);
            const customers = cc.data ?? [];
            if( customers.length === 0 || (customers.length === 1 && customers.includes(organizationId))) {
                onlyOnlineCustomer = true;
            } 
        } 
        if( '' != productId) {
            const pp = await portalApi.settings.organization.getPiceaProductOrganizations( productId);
            const orgs = pp.data ?? [];
            if( orgs.length === 0 || (orgs.length === 1 && orgs.includes(organizationId))) {
                onlyOnlineProduct = true;
            }
        }

        if( '' != productId && '' !== customerId) {
            const products = await portalApi.organization.getCustomerProducts(customerId);
            if( products.successed) {
                const thisProduct = products.data?.find( p => p.product_id === productId)
                _canDeactivateCustomer = (products.data?.length ?? 0) < 2; // only 1 or none products.
                if( thisProduct && thisProduct.solution.solution_id === Solutions.Online) {
                    _canDeactivateProduct = true;
                }
            }
        }
        setCanDeactivateCustomer(_canDeactivateCustomer);
        setCanDeactivateProduct(_canDeactivateProduct);
        if( !_canDeactivateCustomer || !onlyOnlineCustomer) {
            if( !onlyOnlineCustomer)
                setOtherConfirmMessage( strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.ERROR_CUSTOMER);
            else 
                setOtherConfirmMessage( strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.ERROR_PRODUCT);
            
            setConfirmPmc( true);
            return;
        } else {
            if( onlyOnlineProduct && _canDeactivateProduct ) {
                const result = await portalApi.settings.organization.updatePiceaProduct( productId, true);
                console.log( "Product deactivated:", result)
            }
            //  deactivate customer.
            const result = await portalApi.settings.organization.updatePiceaCustomer( customerId, true);
            console.log( "Customer deactivated:", result)
            if( result.successed) {
                deactivateOrganization();
            } else {
                setOtherConfirmMessage( "");
                setConfirmPmc( true);
            }
        }
        
    }, [productId, customerId, organizationId, deactivateOrganization])
    
    if( !initialConfirmationDone) {
        return (<ConfirmProductDeactivation 
                onDismiss={props.onCanceled} 
                onConfirm={deactivatePmcProduct} 
                confirmPhrase={props.organizationName}/>)
    }

    if( confirmPmc) {
        return (
            <Modal isOpen={true} onDismiss={props.onCanceled} containerClassName={DeleteConfirmation_contentStyles.container}>
                <div className={DeleteConfirmation_contentStyles.header}>
                    <span style={{ paddingRight: 32 }}>{strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.TITLE}</span>
                    <IconButton
                        styles={DeleteConfirmation_iconButtonStyles}
                        iconProps={{ iconName: "Cancel" }}
                        ariaLabel="Close"
                        onClick={props.onCanceled}
                    />
                </div>
                <div className={DeleteConfirmation_contentStyles.body}>
                    <Stack tokens={{ childrenGap: 16 }}>
                        <Stack tokens={{ childrenGap: 16 }} >
                            <Stack.Item>
                                <Text>{strings.TRANSACTION.CONTRACT.CUSTOMER} "{props.organizationName}" </Text>
                                { "" === otherConfirmMessage && 
                                    ( <><Text>{strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.CANT_DEACTIVATE}</Text><br/></>) || 
                                    (<><Text>{strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.CANT_DEACTIVATE_BECAUSE}</Text><br/>
                                        <Text>{otherConfirmMessage}</Text><br/></>)
                                }
                                <Text>{strings.ORGANIZATION.SETTINGS.GENERAL.OVERVIEW.DEACTIVATE_ORGANIZATION.CONFIRMATION_QUESTION}</Text>
                            </Stack.Item>
                            <Stack.Item>
                                <Stack.Item>
                                    <Stack horizontal grow horizontalAlign="end" tokens={{ childrenGap: 8 }}>
                                        <SecondaryButton onClick={props.onCanceled} text={strings.BUTTONS.TEXT.NO} />
                                        <DangerButton onClick={onPmcConfirmed}>{strings.BUTTONS.TEXT.YES}</DangerButton>
                                    </Stack>
                                </Stack.Item>
                            </Stack.Item>
                        </Stack>
                    </Stack>
                </div>
            </Modal>
        )
    }

    return (<Spinner />)
}